
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Newspaper, ExternalLink, Calendar } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';

interface NewsItem {
  title: string;
  link: string;
  source: string;
  published: string; // ISO date string
  snippet: string;
}

const categories = [
  { name: "Or", query: "gold price market" },
  { name: "ETFs", query: "etf investing europe" },
  { name: "Banques françaises", query: "french banks finance" }
];

// API key would be better stored in an environment variable
const GOOGLE_NEWS_API_KEY = "DEMO_KEY"; // Replace with your actual API key
const GOOGLE_NEWS_API_URL = "https://newsapi.org/v2/everything";

const fetchNewsForCategory = async (category: string): Promise<NewsItem[]> => {
  try {
    const response = await fetch(`${GOOGLE_NEWS_API_URL}?q=${category}&apiKey=${GOOGLE_NEWS_API_KEY}&language=fr&pageSize=5`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch news');
    }
    
    const data = await response.json();
    
    // Transform to our interface format
    return data.articles.map((article: any) => ({
      title: article.title,
      link: article.url,
      source: article.source.name,
      published: article.publishedAt,
      snippet: article.description || "Aucune description disponible."
    }));
  } catch (error) {
    console.error("Error fetching news:", error);
    // Return mocked data for demonstration when API fails
    return [
      {
        title: `Les dernières tendances du marché de l'or`,
        link: "https://www.lesechos.fr/finance-marches/marches-financiers/or-les-facteurs-qui-pourraient-stimuler-les-cours-1854141",
        source: "Finance Magazine",
        published: new Date().toISOString(),
        snippet: "Les experts prévoient une augmentation continue du prix de l'or dans les mois à venir."
      },
      {
        title: "Nouveaux ETFs lancés sur la bourse de Paris",
        link: "https://www.boursorama.com/bourse/actualites/les-etf-a-la-conquete-des-marches-europeens-01cf5dafaa8339d69869650e13f8c70d",
        source: "Investir Aujourd'hui",
        published: new Date().toISOString(),
        snippet: "Trois nouveaux ETFs thématiques arrivent sur Euronext Paris, ciblant les énergies renouvelables."
      },
      {
        title: "Performance trimestrielle des banques françaises",
        link: "https://www.capital.fr/entreprises-marches/les-banques-francaises-affichent-des-resultats-solides-1444976", 
        source: "Journal Économique",
        published: new Date().toISOString(),
        snippet: "BNP Paribas et Société Générale affichent des résultats supérieurs aux attentes."
      }
    ];
  }
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('fr-FR', { 
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  }).format(date);
};

const NewsCategory = ({ category }: { category: { name: string, query: string } }) => {
  const { data: news, isLoading, error } = useQuery({
    queryKey: ['financialNews', category.query],
    queryFn: () => fetchNewsForCategory(category.query),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  if (isLoading) {
    return (
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Newspaper className="h-5 w-5 text-patrimoine-gold" />
            {category.name}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-center py-4 text-muted-foreground">Chargement des actualités...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Newspaper className="h-5 w-5 text-patrimoine-gold" />
            {category.name}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-center py-4 text-red-500">Impossible de charger les actualités</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm h-full">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2">
          <Newspaper className="h-5 w-5 text-patrimoine-gold" />
          {category.name}
        </CardTitle>
        <CardDescription>Les dernières actualités financières</CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[380px] pr-4">
          <div className="space-y-4">
            {news?.map((item, index) => (
              <div key={index} className="border-b border-gray-100 pb-3 last:border-0">
                <h4 className="font-medium text-base">{item.title}</h4>
                <p className="text-sm text-muted-foreground my-1 line-clamp-2">{item.snippet}</p>
                <div className="flex items-center justify-between mt-2 text-xs">
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    <span>{formatDate(item.published)}</span>
                  </div>
                  <a 
                    href={item.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-patrimoine-gold hover:underline"
                  >
                    <span>Lire plus</span>
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

const FinancialNewsSection = () => {
  return (
    <section className="py-12 md:py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <h2 className="section-title text-center mb-10">Actualités Financières</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {categories.map((category) => (
            <NewsCategory key={category.name} category={category} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FinancialNewsSection;
